/**
 * SSR 兼容性工具函数
 * 用于在服务端渲染时安全地访问浏览器 API
 */

/**
 * 检查是否在客户端环境
 */
export function isClient(): boolean {
  return typeof window !== 'undefined';
}

/**
 * 检查是否在服务端环境
 */
export function isServer(): boolean {
  return typeof window === 'undefined';
}

/**
 * 安全地访问 window 对象
 */
export function getWindow(): Window | undefined {
  return typeof window !== 'undefined' ? window : undefined;
}

/**
 * 安全地访问 document 对象
 */
export function getDocument(): Document | undefined {
  return typeof document !== 'undefined' ? document : undefined;
}

/**
 * 安全地访问 localStorage
 */
export function getLocalStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * 安全地访问 sessionStorage
 */
export function getSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * 安全地执行客户端代码
 * @param fn - 只在客户端执行的函数
 * @param fallback - 服务端时的返回值
 */
export function clientOnly<T>(fn: () => T, fallback?: T): T | undefined {
  if (isClient()) {
    return fn();
  }
  return fallback;
}

/**
 * 在客户端执行回调函数
 * @param fn - 回调函数
 */
export function onClient(fn: () => void): void {
  if (isClient()) {
    fn();
  }
}

/**
 * 安全地获取元素
 */
export function safeGetElementById(id: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.getElementById(id);
}

/**
 * 安全地设置 body 样式
 */
export function safeSetBodyStyle(property: string, value: string): void {
  if (typeof document === 'undefined') return;
  document.body.style.setProperty(property, value);
}

/**
 * 安全地移除 body 样式
 */
export function safeRemoveBodyStyle(property: string): void {
  if (typeof document === 'undefined') return;
  document.body.style.removeProperty(property);
}
